import React, { useState } from 'react';
import { Calendar, Clock, Send, X, Users, MessageSquare } from 'lucide-react';
import EXAM_API from '../../api/services/examApi';
import toast from 'react-hot-toast';

const ScheduleNoticeModal = ({ isOpen, onClose, exam }) => {
  const [mode, setMode] = useState('now'); // 'now' | 'schedule'
  const [scheduleData, setScheduleData] = useState({
    date: '',
    time: ''
  });
  const [loading, setLoading] = useState(false);
  const [audience, setAudience] = useState('both');

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let payload = {
        sendWhatsapp: true,
        targetAudience: audience
      };

      if (mode === 'schedule') {
        if (!scheduleData.date || !scheduleData.time) {
          toast.error("Please select date and time");
          setLoading(false);
          return;
        }
        // Combine Date & Time into ISO String
        const combinedDate = new Date(`${scheduleData.date}T${scheduleData.time}`);
        payload.scheduledDate = combinedDate.toISOString();
      }

      await EXAM_API.createExamNotice(exam._id, payload);
      
      toast.success(mode === 'now' ? 'Notice sent!' : 'Notice scheduled successfully!');
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare size={20} className="text-[var(--primary-color)]" />
              Send Exam Notice
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Notify students & parents</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full transition-colors text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-3">
                <Calendar className="text-blue-600 shrink-0 mt-0.5" size={16} />
                <p className="text-sm text-blue-900">
                    You are sending a notice for <span className="font-bold">{exam?.examName}</span>.
                </p>
            </div>

            {/* Mode Selection */}
            <div className="grid grid-cols-2 gap-3 p-1 bg-gray-100 rounded-xl">
              <button 
                onClick={() => setMode('now')}
                className={`py-2.5 px-4 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${mode === 'now' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Send size={14} /> Send Now
              </button>
              <button 
                onClick={() => setMode('schedule')}
                className={`py-2.5 px-4 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${mode === 'schedule' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <Clock size={14} /> Schedule
              </button>
            </div>

            {/* Schedule Inputs */}
            {mode === 'schedule' && (
              <div className="space-y-4 animate-in slide-in-from-top-2">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Date</label>
                      <input 
                        type="date" 
                        className="w-full bg-gray-50 border border-gray-200 p-2.5 rounded-lg text-sm font-medium focus:border-[var(--primary-color)] outline-none transition-all"
                        min={new Date().toISOString().split('T')[0]}
                        onChange={e => setScheduleData({...scheduleData, date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Time</label>
                      <input 
                        type="time" 
                        className="w-full bg-gray-50 border border-gray-200 p-2.5 rounded-lg text-sm font-medium focus:border-[var(--primary-color)] outline-none transition-all"
                        onChange={e => setScheduleData({...scheduleData, time: e.target.value})}
                      />
                    </div>
                </div>
              </div>
            )}

            {/* Audience Selection */}
            <div className="space-y-1.5">
               <label className="flex items-center gap-2 text-[10px] uppercase font-bold text-gray-500 tracking-wider">
                  <Users size={12} /> Send To
               </label>
               <select 
                 className="w-full bg-white border border-gray-300 p-3 rounded-xl text-sm font-medium text-gray-900 focus:border-[var(--primary-color)] outline-none transition-all appearance-none"
                 value={audience}
                 onChange={(e) => setAudience(e.target.value)}
                 style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
               >
                 <option value="both">Both Students & Parents</option>
                 <option value="student">Students Only</option>
                 <option value="parent">Parents Only</option>
               </select>
            </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button 
            onClick={onClose} 
            className="px-5 py-2.5 text-sm font-bold text-gray-600 hover:bg-white hover:text-gray-900 border border-transparent hover:border-gray-200 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2 text-sm font-bold shadow-lg shadow-gray-900/20 transition-all transform active:scale-95"
            style={{ backgroundColor: 'var(--primary-color)' }}
          >
            {loading ? 'Processing...' : (
              <>{mode === 'now' ? <Send size={16}/> : <Clock size={16}/>} Confirm</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
export default ScheduleNoticeModal;
